// Sijaintilähteet yhteisen rajapinnan takana:
//
//   watch(onLocation, onError) -> Promise<stop()>
//
// 1. Puhelinsovellus (Capacitor): natiivi taustapaikannus. Android näyttää
//    pysyvän ilmoituksen, ja paikannus jatkuu näyttö sammutettuna
//    (hyväksymiskriteeri 4). Tämä polku on testattava oikealla laitteella.
// 2. Selain: navigator.geolocation. Toimii paikallisessa testauksessa, mutta
//    selain voi pysäyttää paikannuksen, kun näyttö sammuu.
// 3. Simulaattori: kehitystä varten, sijainnit tulevat ohjelmallisesti.

import { registerPlugin } from "@capacitor/core";
import { isNativeApp } from "./api.js";

const BackgroundGeolocation = registerPlugin("BackgroundGeolocation");

function nativeSource() {
  return {
    kind: "native",
    async watch(onLocation, onError) {
      const id = await BackgroundGeolocation.addWatcher(
        {
          backgroundTitle: "Tilehunting tallentaa retkeä",
          backgroundMessage: "Lopeta tallennus sovelluksesta, kun retki päättyy.",
          requestPermissions: true,
          stale: false,
          distanceFilter: 5, // metriä; tiheämpi ei paranna ruutujen tunnistusta
        },
        (location, error) => {
          if (error) {
            onError(error.code === "NOT_AUTHORIZED"
              ? { message: "Sijaintilupa puuttuu. Salli sijainti puhelimen asetuksista.", openSettings: () => BackgroundGeolocation.openSettings() }
              : { message: error.message || "Sijaintia ei saatu." });
            return;
          }
          onLocation({ latitude: location.latitude, longitude: location.longitude, accuracy: location.accuracy, time: location.time ?? Date.now() });
        },
      );
      return () => BackgroundGeolocation.removeWatcher({ id });
    },
  };
}

function browserSource() {
  return {
    kind: "browser",
    async watch(onLocation, onError) {
      if (!("geolocation" in navigator)) {
        onError({ message: "Tämä selain ei tue sijaintia." });
        return () => {};
      }
      // Näytön pitäminen päällä parantaa selaintallennusta. Puhelinsovellus
      // ei tarvitse tätä, koska se tallentaa taustalla.
      let wakeLock = null;
      try {
        wakeLock = await navigator.wakeLock?.request("screen");
      } catch {
        /* ei tuettu tai ei sallittu: tallennus toimii silti näytön ollessa päällä */
      }
      const id = navigator.geolocation.watchPosition(
        (pos) => onLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy, time: pos.timestamp }),
        (err) => onError({ message: err.code === 1 ? "Sijaintilupa evättiin. Salli sijainti selaimen asetuksista." : "Sijaintia ei saatu juuri nyt." }),
        { enableHighAccuracy: true, maximumAge: 0, timeout: 30_000 },
      );
      return () => {
        navigator.geolocation.clearWatch(id);
        wakeLock?.release?.().catch(() => {});
      };
    },
  };
}

/**
 * Simulaattori: pelaaja "kävelee" kohti napautettua karttapistettä.
 * Käytössä vain, kun osoitteessa on ?dev=1.
 */
export function simulatorSource() {
  let listener = null;
  return {
    kind: "simulator",
    async watch(onLocation) {
      listener = onLocation;
      return () => (listener = null);
    },
    emit(latitude, longitude, time = Date.now()) {
      listener?.({ latitude, longitude, accuracy: 5, time });
    },
    get active() {
      return listener !== null;
    },
  };
}

export function defaultLocationSource() {
  return isNativeApp() ? nativeSource() : browserSource();
}

/**
 * Nykyinen sijainti kertahaulla tarkkuuden kanssa: sovelluksen tuorein sijainti (`known`, muotoa
 * { latitude, longitude, accuracy }) tai selaimen kertahaku. Palauttaa { lat, lon, accuracy } (metriä,
 * null jos laite ei kerro). Paikan päällä tehtävät asetukset (kohteen piste) tarvitsevat tarkkuuden,
 * jotta heikko paikannus voidaan huomauttaa ennen tallennusta.
 */
export function currentFix(known) {
  if (known) return Promise.resolve({ lat: known.latitude, lon: known.longitude, accuracy: known.accuracy ?? null });
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Sijainti ei ole käytettävissä tässä laitteessa."));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lon: p.coords.longitude, accuracy: p.coords.accuracy ?? null }),
      () => reject(new Error("Sijaintia ei saatu. Salli sijainti ja yritä uudelleen.")),
      // Tarkka paikannus: pelkkä verkkopaikannus ei aina anna sijaintia (esim. maastossa, emulaattorissa).
      // maximumAge 0: pisteen asetukseen kelpaa vain tuore sijainti, ei välimuistissa oleva.
      { enableHighAccuracy: true, timeout: 20_000, maximumAge: 0 },
    );
  });
}

/**
 * Nykyinen sijainti kertahaulla: sovelluksen tuorein sijainti (`known`, muotoa
 * { latitude, longitude }) tai selaimen kertahaku. Palauttaa { lat, lon }.
 */
export function currentPosition(known) {
  if (known) return Promise.resolve({ lat: known.latitude, lon: known.longitude });
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("Sijainti ei ole käytettävissä tässä laitteessa."));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lon: p.coords.longitude }),
      () => reject(new Error("Sijaintia ei saatu. Salli sijainti ja yritä uudelleen.")),
      // Tarkka paikannus: pelkkä verkkopaikannus ei aina anna sijaintia (esim. maastossa, emulaattorissa).
      { enableHighAccuracy: true, timeout: 20_000, maximumAge: 30_000 },
    );
  });
}

/**
 * Tuore sijainti paikan päällä tehtävään asetukseen: selaimen/laitteen tuore haku. Jos sitä ei saada
 * (esim. paikannus hetkeksi poissa), käytetään sovelluksen tuoreinta tunnettua sijaintia `me`; jos se on
 * vanhempi kuin STALE_AFTER_MS, tulos on merkitty `stale: true` eikä sitä saa pitää maastotarkistuksena.
 * Simulaattorissa käytetään aina simuloitua sijaintia.
 */
export async function fixNow({ simulator = false, me = null, now = Date.now() } = {}) {
  if (simulator && me) return { ...(await currentFix(me)), stale: false };
  try {
    return { ...(await currentFix(null)), stale: false };
  } catch (e) {
    if (!me) throw e;
    // Varasijainti: sovelluksen tuorein tunnettu sijainti. Sen ikä lasketaan, ja liian vanha (tai aikaleimaton)
    // sijainti merkitään vanhentuneeksi, jotta se ei vaikuta paikan päällä tehdyltä tarkistukselta.
    const ageMs = Number.isFinite(me.at) ? Math.max(0, now - me.at) : Infinity;
    return { ...(await currentFix(me)), stale: ageMs > STALE_AFTER_MS, ageS: Number.isFinite(ageMs) ? Math.round(ageMs / 1000) : null };
  }
}

/** Näin vanha tunnettu sijainti (ms) kelpaa vielä tuoreena paikan päällä tehtävään asetukseen. */
export const STALE_AFTER_MS = 30_000;
